import { useContext, useMemo, useState } from "react";
import { NotasContext, hoyISO } from "../../../context/NotasContext";
import { AlquileresContext } from "../../../context/AlquileresContext";
import { HouseContext } from "../../../context/HouseContext";
import { useAuthContext } from "../../../context/authContext";
import { nombreCompleto } from "../../../utils/nombre";
import SearchableSelect from "../../../components/SearchableSelect";
import ConfirmDialog from "../../../components/ConfirmDialog/ConfirmDialog";
import ModalPortal from "../../../components/ModalPortal";
import LoadingState from "../../../components/LoadingState/LoadingState";
import {
  FaPlusCircle,
  FaSearch,
  FaTimes,
  FaTrash,
  FaPen,
  FaThumbtack,
  FaCheck,
  FaUndo,
  FaBell,
  FaLink,
  FaStickyNote,
  FaExclamationTriangle,
} from "react-icons/fa";

const CATEGORIAS = [
  "General",
  "Pendiente",
  "Llamado",
  "Reclamo",
  "Mantenimiento",
  "Cobranza",
  "Visita",
  "Importante",
];

// Cada color define el fondo/borde de la tarjeta en claro y oscuro.
const COLORES = [
  { id: "amarillo", label: "Amarillo", swatch: "bg-amber-300", card: "bg-amber-50 border-amber-200 dark:bg-amber-500/10 dark:border-amber-500/30" },
  { id: "celeste", label: "Celeste", swatch: "bg-sky-300", card: "bg-sky-50 border-sky-200 dark:bg-sky-500/10 dark:border-sky-500/30" },
  { id: "verde", label: "Verde", swatch: "bg-emerald-300", card: "bg-emerald-50 border-emerald-200 dark:bg-emerald-500/10 dark:border-emerald-500/30" },
  { id: "rosa", label: "Rosa", swatch: "bg-pink-300", card: "bg-pink-50 border-pink-200 dark:bg-pink-500/10 dark:border-pink-500/30" },
  { id: "violeta", label: "Violeta", swatch: "bg-violet-300", card: "bg-violet-50 border-violet-200 dark:bg-violet-500/10 dark:border-violet-500/30" },
  { id: "gris", label: "Gris", swatch: "bg-gray-300", card: "bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700" },
];

const colorCard = (id) =>
  (COLORES.find((c) => c.id === id) || COLORES[COLORES.length - 1]).card;

const TIPOS_VINCULO = [
  { id: "", label: "Sin vincular" },
  { id: "CONTRATO", label: "Contrato" },
  { id: "PROPIEDAD", label: "Propiedad" },
  { id: "LOCADOR", label: "Locador (Propietario)" },
  { id: "LOCATARIO", label: "Locatario (Inquilino)" },
];

const FORM_VACIO = {
  titulo: "",
  contenido: "",
  categoria: "General",
  color: "amarillo",
  recordatorio: "",
  vinculoTipo: "",
  vinculoId: "",
};

// "hace 2 dias" a partir de un ISO string.
const tiempoRelativo = (iso) => {
  if (!iso) return "";
  const fecha = new Date(iso);
  if (Number.isNaN(fecha.getTime())) return "";
  const dias = Math.floor((Date.now() - fecha.getTime()) / 86400000);
  if (dias <= 0) return "hoy";
  if (dias === 1) return "ayer";
  if (dias < 30) return `hace ${dias} dias`;
  return fecha.toLocaleDateString("es-AR");
};

const formatFechaCorta = (yyyymmdd) => {
  if (!yyyymmdd) return "";
  const [a, m, d] = yyyymmdd.split("-");
  return `${d}/${m}/${a}`;
};

function Notas() {
  const { notas, initialLoaded, crearNota, editarNota, borrarNota } =
    useContext(NotasContext);
  const { contratos, locadores, locatarios } = useContext(AlquileresContext);
  const { allHouses } = useContext(HouseContext);
  const { user } = useAuthContext();

  const [search, setSearch] = useState("");
  const [categoria, setCategoria] = useState("Todas");
  const [estado, setEstado] = useState("TODAS"); // TODAS | PENDIENTE | HECHA
  const [soloMias, setSoloMias] = useState(false);

  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(null); // idFirebase o null
  const [form, setForm] = useState(FORM_VACIO);
  const [notaABorrar, setNotaABorrar] = useState(null);
  // Las escrituras son optimistas: si alguna falla, el cambio se revierte solo
  // y avisamos aca arriba.
  const [error, setError] = useState("");

  const hoy = hoyISO();

  // Opciones del selector de vinculo segun el tipo elegido. Cada item se
  // normaliza a { id, label } para poder usar un unico SearchableSelect.
  const opcionesVinculo = useMemo(() => {
    switch (form.vinculoTipo) {
      case "CONTRATO":
        return contratos.map((c) => ({
          id: c.idFirebase,
          label: [c.propiedad, [c.locadorNombre, c.locatarioNombre].filter(Boolean).join(" / ")]
            .filter(Boolean)
            .join(" - "),
        }));
      case "PROPIEDAD":
        return allHouses.map((h) => ({
          id: h.idFirebase,
          label: [h.name, h.address].filter(Boolean).join(" - "),
        }));
      case "LOCADOR":
        return locadores.map((l) => ({
          id: l.idFirebase,
          label: nombreCompleto(l) || l.nombre || "(sin nombre)",
        }));
      case "LOCATARIO":
        return locatarios.map((l) => ({
          id: l.idFirebase,
          label: nombreCompleto(l) || l.nombre || "(sin nombre)",
        }));
      default:
        return [];
    }
  }, [form.vinculoTipo, contratos, allHouses, locadores, locatarios]);

  const notasVisibles = useMemo(() => {
    const s = search.trim().toLowerCase();
    const filtradas = notas.filter((n) => {
      if (soloMias && n.autorUid !== user?.uid) return false;
      if (categoria !== "Todas" && (n.categoria || "General") !== categoria) return false;
      if (estado === "PENDIENTE" && n.estado === "HECHA") return false;
      if (estado === "HECHA" && n.estado !== "HECHA") return false;
      if (!s) return true;
      return (
        n.titulo?.toLowerCase().includes(s) ||
        n.contenido?.toLowerCase().includes(s) ||
        n.categoria?.toLowerCase().includes(s) ||
        n.vinculoLabel?.toLowerCase().includes(s) ||
        n.autorNombre?.toLowerCase().includes(s)
      );
    });

    // Fijadas arriba, luego pendientes, y dentro de cada grupo la mas reciente.
    return filtradas.sort((a, b) => {
      if (!!b.fijada !== !!a.fijada) return b.fijada ? 1 : -1;
      const hechaA = a.estado === "HECHA";
      const hechaB = b.estado === "HECHA";
      if (hechaA !== hechaB) return hechaA ? 1 : -1;
      return String(b.updatedAt || "").localeCompare(String(a.updatedAt || ""));
    });
  }, [notas, search, categoria, estado, soloMias, user]);

  const totales = useMemo(
    () => ({
      total: notas.length,
      pendientes: notas.filter((n) => n.estado !== "HECHA").length,
      vencidas: notas.filter(
        (n) => n.estado !== "HECHA" && n.recordatorio && n.recordatorio <= hoy
      ).length,
    }),
    [notas, hoy]
  );

  const abrirNueva = () => {
    setEditando(null);
    setForm(FORM_VACIO);
    setModal(true);
  };

  const abrirEdicion = (nota) => {
    setEditando(nota.idFirebase);
    setForm({
      titulo: nota.titulo || "",
      contenido: nota.contenido || "",
      categoria: nota.categoria || "General",
      color: nota.color || "amarillo",
      recordatorio: nota.recordatorio || "",
      vinculoTipo: nota.vinculoTipo || "",
      vinculoId: nota.vinculoId || "",
    });
    setModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.contenido.trim()) return;

    const idEditando = editando;
    const vinculoLabel = form.vinculoTipo
      ? opcionesVinculo.find((o) => o.id === form.vinculoId)?.label || ""
      : "";
    const datos = {
      ...form,
      titulo: form.titulo.trim(),
      contenido: form.contenido.trim(),
      vinculoLabel,
      vinculoId: form.vinculoTipo ? form.vinculoId : "",
    };

    // Cerramos el modal en el acto; la nota ya se ve en la grilla mientras la
    // escritura viaja.
    setModal(false);
    setEditando(null);
    setError("");
    try {
      if (idEditando) {
        await editarNota(idEditando, datos);
      } else {
        await crearNota({
          ...datos,
          estado: "PENDIENTE",
          fijada: false,
          autorUid: user?.uid || "",
          autorNombre: user?.displayName || user?.email || "",
        });
      }
    } catch {
      setError("No se pudo guardar la nota. Revisa la conexion e intenta de nuevo.");
    }
  };

  const togglePatch = async (nota, patch) => {
    setError("");
    try {
      await editarNota(nota.idFirebase, patch);
    } catch {
      setError("No se pudo actualizar la nota. Revisa la conexion e intenta de nuevo.");
    }
  };

  const handleDelete = async () => {
    if (!notaABorrar) return;
    const id = notaABorrar.idFirebase;
    setNotaABorrar(null);
    setError("");
    try {
      await borrarNota(id);
    } catch {
      setError("No se pudo borrar la nota. Revisa la conexion e intenta de nuevo.");
    }
  };

  const inputClass =
    "w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent";

  if (!initialLoaded) return <LoadingState message="Cargando notas..." />;

  return (
    // El contenedor del dashboard mide 93vh con py-6 (3rem), asi que ese es el
    // alto disponible: lo usamos como minimo para que el estado vacio estire.
    <div className="px-4 md:px-6 py-4 space-y-4 flex flex-col min-h-[calc(93vh-3rem)]">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Notas</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {totales.total} en total &middot; {totales.pendientes} pendientes
            {totales.vencidas > 0 && (
              <span className="text-red-500 font-medium"> &middot; {totales.vencidas} con recordatorio vencido</span>
            )}
          </p>
        </div>
        <button
          onClick={abrirNueva}
          className="inline-flex items-center gap-2 bg-primary hover:bg-secondary text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
        >
          <FaPlusCircle className="w-4 h-4" /> Nueva Nota
        </button>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-400">
          <FaExclamationTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span className="flex-1">{error}</span>
          <button
            onClick={() => setError("")}
            className="p-0.5 text-red-400 hover:text-red-600 dark:hover:text-red-300"
          >
            <FaTimes className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row flex-wrap gap-3">
        <div className="relative flex-1 min-w-0 sm:max-w-sm">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Buscar en titulo, texto, vinculo..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
        <select
          value={categoria}
          onChange={(e) => setCategoria(e.target.value)}
          className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100"
        >
          <option value="Todas">Todas las categorias</option>
          {CATEGORIAS.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select
          value={estado}
          onChange={(e) => setEstado(e.target.value)}
          className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100"
        >
          <option value="TODAS">Todas</option>
          <option value="PENDIENTE">Pendientes</option>
          <option value="HECHA">Hechas</option>
        </select>
        <button
          onClick={() => setSoloMias(!soloMias)}
          className={`text-sm px-3 py-2 rounded-lg border transition-colors ${
            soloMias
              ? "bg-primary text-white border-primary"
              : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
          }`}
        >
          Solo mias
        </button>
      </div>

      {/* Grilla de notas */}
      {notasVisibles.length === 0 ? (
        <div className="flex-1 bg-white dark:bg-gray-800 rounded-xl shadow-sm py-16 flex flex-col items-center justify-center text-center px-4">
          <FaStickyNote className="w-8 h-8 text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-gray-500 dark:text-gray-400">
            {notas.length === 0
              ? "Todavia no hay notas. Crea la primera con \"Nueva Nota\"."
              : "Ninguna nota coincide con los filtros."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {notasVisibles.map((nota) => {
            const hecha = nota.estado === "HECHA";
            const vencida = !hecha && nota.recordatorio && nota.recordatorio < hoy;
            const esHoy = !hecha && nota.recordatorio === hoy;
            return (
              <div
                key={nota.idFirebase}
                className={`rounded-xl border shadow-sm p-4 flex flex-col gap-3 transition-opacity ${colorCard(
                  nota.color
                )} ${hecha ? "opacity-60" : ""}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    {nota.fijada && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-primary dark:text-accent mb-1">
                        <FaThumbtack className="w-3 h-3" /> Fijada
                      </span>
                    )}
                    {nota.titulo && (
                      <h3
                        className={`font-semibold text-gray-800 dark:text-gray-100 break-words ${
                          hecha ? "line-through" : ""
                        }`}
                      >
                        {nota.titulo}
                      </h3>
                    )}
                  </div>
                  <button
                    onClick={() => togglePatch(nota, { fijada: !nota.fijada })}
                    title={nota.fijada ? "Quitar de fijadas" : "Fijar arriba"}
                    className={`shrink-0 p-1.5 rounded-lg transition-colors ${
                      nota.fijada
                        ? "text-primary dark:text-accent"
                        : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    }`}
                  >
                    <FaThumbtack className="w-3.5 h-3.5" />
                  </button>
                </div>

                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                  {nota.contenido}
                </p>

                <div className="flex flex-wrap gap-1.5">
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-black/5 dark:bg-white/10 text-gray-600 dark:text-gray-300">
                    {nota.categoria || "General"}
                  </span>
                  {nota.vinculoLabel && (
                    <span
                      title={nota.vinculoLabel}
                      className="inline-flex items-center gap-1 max-w-full text-xs font-medium px-2 py-0.5 rounded-full bg-black/5 dark:bg-white/10 text-gray-600 dark:text-gray-300"
                    >
                      <FaLink className="w-2.5 h-2.5 shrink-0" />
                      <span className="truncate">{nota.vinculoLabel}</span>
                    </span>
                  )}
                  {nota.recordatorio && (
                    <span
                      className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                        vencida
                          ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                          : esHoy
                          ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                          : "bg-black/5 dark:bg-white/10 text-gray-600 dark:text-gray-300"
                      }`}
                    >
                      <FaBell className="w-2.5 h-2.5" />
                      {formatFechaCorta(nota.recordatorio)}
                    </span>
                  )}
                </div>

                <div className="flex items-center justify-between gap-2 pt-2 border-t border-black/5 dark:border-white/10">
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                    {nota.autorNombre || "Sin autor"} &middot; {tiempoRelativo(nota.updatedAt || nota.createdAt)}
                  </p>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() =>
                        togglePatch(nota, { estado: hecha ? "PENDIENTE" : "HECHA" })
                      }
                      title={hecha ? "Marcar como pendiente" : "Marcar como hecha"}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-600 transition-colors"
                    >
                      {hecha ? <FaUndo className="w-3.5 h-3.5" /> : <FaCheck className="w-3.5 h-3.5" />}
                    </button>
                    <button
                      onClick={() => abrirEdicion(nota)}
                      title="Editar"
                      className="p-1.5 rounded-lg text-gray-400 hover:text-primary transition-colors"
                    >
                      <FaPen className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setNotaABorrar(nota)}
                      title="Borrar"
                      className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <FaTrash className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal crear / editar */}
      {modal && (
        <ModalPortal>
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4 py-6 overflow-y-auto">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl p-6 sm:p-8 my-auto">
              <div className="flex items-center justify-between pb-4 mb-5 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100">
                  {editando ? "Editar Nota" : "Nueva Nota"}
                </h3>
                <button
                  onClick={() => setModal(false)}
                  className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <FaTimes />
                </button>
              </div>
              <form onSubmit={handleSave} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Titulo
                  </label>
                  <input
                    type="text"
                    value={form.titulo}
                    onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                    className={inputClass}
                    placeholder="Ej: Llamar a Perez por el aumento"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Nota *
                  </label>
                  <textarea
                    value={form.contenido}
                    onChange={(e) => setForm({ ...form, contenido: e.target.value })}
                    className={`${inputClass} min-h-[120px] resize-y`}
                    placeholder="Escribi la observacion, el detalle del llamado, lo que quedo pendiente..."
                    required
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Categoria
                    </label>
                    <select
                      value={form.categoria}
                      onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                      className={inputClass}
                    >
                      {CATEGORIAS.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Recordatorio (opcional)
                    </label>
                    <input
                      type="date"
                      value={form.recordatorio}
                      onChange={(e) => setForm({ ...form, recordatorio: e.target.value })}
                      className={inputClass}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Vincular a
                    </label>
                    <select
                      value={form.vinculoTipo}
                      onChange={(e) =>
                        setForm({ ...form, vinculoTipo: e.target.value, vinculoId: "" })
                      }
                      className={inputClass}
                    >
                      {TIPOS_VINCULO.map((t) => (
                        <option key={t.id} value={t.id}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  {form.vinculoTipo && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {TIPOS_VINCULO.find((t) => t.id === form.vinculoTipo)?.label}
                      </label>
                      <SearchableSelect
                        options={opcionesVinculo}
                        value={form.vinculoId}
                        onChange={(id) => setForm({ ...form, vinculoId: id })}
                        getLabel={(o) => o.label}
                        getKey={(o) => o.id}
                        placeholder="Buscar..."
                        clearable
                      />
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Color
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {COLORES.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setForm({ ...form, color: c.id })}
                        title={c.label}
                        className={`w-8 h-8 rounded-full ${c.swatch} border-2 transition-transform ${
                          form.color === c.id
                            ? "border-primary scale-110"
                            : "border-transparent hover:scale-105"
                        }`}
                      />
                    ))}
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4 mt-2 border-t border-gray-100 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => setModal(false)}
                    className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm bg-primary hover:bg-secondary text-white rounded-lg"
                  >
                    {editando ? "Guardar cambios" : "Guardar Nota"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </ModalPortal>
      )}

      <ConfirmDialog
        isOpen={!!notaABorrar}
        title="Borrar nota"
        message="Esta nota se va a eliminar para todos. ¿Continuar?"
        confirmLabel="Borrar"
        onConfirm={handleDelete}
        onCancel={() => setNotaABorrar(null)}
      />
    </div>
  );
}

export default Notas;
